import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { apiGet, apiPost } from '../../utils/api';
import './InsightsView.css';

// 기업 인사이트(상단 메뉴): 표를 보는 곳이 아니라 이 기업의 인력·인건비 흐름을 읽는 곳이다.
//   맨 위: 핵심 숫자(재직 인원·올해 입퇴사·평균 근속·이번 달 인건비)
//   가운데: 인원 추이 · 입퇴사 흐름 · 인건비 추이
//   아래: 챙겨야 할 것(1년 도래·계약 종료·만 60세·휴직 등) · 최근 변화 · 직원 목록(누르면 오른쪽에 상세)
// 계약 변경은 달마다 저장된 실제 계약 값을 직전 달과 비교한 것이다(서버가 계산).

const API = '/api/payroll';
const SHEET_LABEL = { fulltime: '상용직', daily: '일용직', business: '사업소득' };
const STATUS_TONE = { 재직: 'on', '명단에 없음': 'gone', 퇴사: 'left' };
// 계열 색: 데이터 시각화 기본 팔레트의 앞 세 칸(색맹 대비 검증된 순서)
const SERIES = { fulltime: '#2a78d6', daily: '#eb6834', business: '#1baf7a' };
const HIRE = '#2a78d6';
const EXIT = '#eb6834';
const OP_LABEL = {
  identity_renamed: '성명 변경', identity_rrn_filled: '주민번호 입력', identity_rrn_changed: '주민번호 변경',
  employee_merged: '직원 병합', employee_assigned: '줄 연결',
};
const DAY = 24 * 60 * 60 * 1000;

const won = (v) => `${Math.round(v || 0).toLocaleString('ko-KR')}원`;
const manwon = (v) => `${Math.round((v || 0) / 10000).toLocaleString('ko-KR')}만`;
const fmt = (type, value) => {
  if (value === null || value === undefined || value === '') return '(없음)';
  if (typeof value === 'number') return type === 'money' ? won(value) : (Math.round(value * 100) / 100).toLocaleString('ko-KR');
  return String(value);
};
const toDate = (text) => {
  const d = new Date(`${String(text).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addYears = (d, n) => { const x = new Date(d); x.setFullYear(x.getFullYear() + n); return x; };
const nextDay = (text) => { const d = toDate(text); return d ? iso(new Date(d.getTime() + DAY)) : ''; };
const shiftYm = (ym, delta) => {
  const total = Number(ym.slice(0, 4)) * 12 + Number(ym.slice(5)) - 1 + delta;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
};
const ymShort = (ym) => `${ym.slice(2, 4)}.${ym.slice(5)}`;
const tenureMonths = (hire, until) => {
  const s = toDate(hire);
  const e = until ? toDate(until) : new Date();
  if (!s || !e || e < s) return null;
  let m = (e.getFullYear() - s.getFullYear()) * 12 + e.getMonth() - s.getMonth();
  if (e.getDate() < s.getDate()) m -= 1;
  return m;
};
const tenureText = (months) => {
  if (months == null) return '–';
  const y = Math.floor(months / 12);
  const m = months % 12;
  return [y ? `${y}년` : '', m || !y ? `${m}개월` : ''].filter(Boolean).join(' ');
};
const parseYm = (ym) => ({ year: Number(ym.slice(0, 4)), month: Number(ym.slice(5)) });
const dday = (d, today) => { const n = Math.round((d - today) / DAY); return n === 0 ? 'D-day' : n > 0 ? `D-${n}` : `${-n}일 지남`; };

// ---------------------------------------------------------------- 핵심 숫자
function Stat({ label, value, sub, tone }) {
  return (
    <div className="ins-stat">
      <div className="ins-stat-label">{label}</div>
      <div className="ins-stat-value">{value}</div>
      {sub && <div className={`ins-stat-sub ${tone || ''}`}>{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- 그래프 공통
function ChartCard({ title, note, children }) {
  return (
    <section className="ins-card ins-chart">
      <header><h4>{title}</h4>{note && <span className="ins-muted">{note}</span>}</header>
      <div className="ins-chart-body">{children}</div>
    </section>
  );
}

function ChartTip({ active, payload, label, money }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="ins-tip">
      <b>{label}</b>
      {payload.map((p) => (
        <div key={p.dataKey}><i style={{ background: p.color }} />{p.name} <span>{money ? won(p.value) : `${Math.abs(p.value)}명`}</span></div>
      ))}
    </div>
  );
}

const axis = { fontSize: 11, fill: '#6c757d' };

// ---------------------------------------------------------------- 오른쪽 상세(서랍)
function Detail({ person, suspects, people, companyId, months, onOpen, onMerged, onClose }) {
  const [arm, setArm] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const personId = person ? person.employee_id : null;
  useEffect(() => { setArm(null); setError(''); }, [personId]);
  if (!person) return null;

  const group = suspects.find((s) => s.employee_ids.includes(person.employee_id));
  const others = group ? group.employee_ids.filter((id) => id !== person.employee_id).map((id) => people.get(id)).filter(Boolean) : [];
  const merge = async (other) => {
    setBusy(true);
    setError('');
    try {
      await apiPost(`${API}/companies/${companyId}/employees/merge`, { keep_employee_id: person.employee_id, merge_employee_id: other.employee_id });
      onMerged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const strip = months.slice(-24);

  return (
    <>
      <div className="ins-drawer-back" onClick={onClose} />
      <aside className="ins-drawer" role="dialog" aria-label={`${person.name} 상세`}>
        <div className="ins-head">
          <div>
            <h4>{person.name} <span className={`ins-pill ${STATUS_TONE[person.status]}`}>{person.status}</span></h4>
            <div className="ins-sub">{person.id_number || '주민번호 없음'} · {SHEET_LABEL[person.last_sheet]}{person.age != null && ` · 만 ${person.age}세`}</div>
          </div>
          <button type="button" className="ins-close" onClick={onClose} title="닫기">✕</button>
        </div>

        <dl className="ins-facts">
          <dt>입사일</dt><dd>{person.hire_date || '–'}</dd>
          <dt>퇴사일</dt>
          <dd>{person.leave_date ? <>{person.leave_date} <span className="ins-muted">(마지막 근무일 · 퇴직일 {nextDay(person.leave_date)})</span></> : '–'}</dd>
          <dt>근속</dt><dd>{tenureText(tenureMonths(person.hire_date, person.leave_date))}</dd>
          <dt>최근 지급</dt><dd>{person.last_pay != null ? `${won(person.last_pay)} (${person.last})` : '–'}</dd>
        </dl>

        <div className="ins-strip" title="최근 24개월 대장에 있던 달">
          {strip.map((ym) => {
            const places = person.months[ym];
            return (
              <button
                type="button"
                key={ym}
                className={places ? `on sheet-${places[0].sheet}` : ''}
                disabled={!places}
                title={places ? `${ym} ${SHEET_LABEL[places[0].sheet]} · 누르면 그 달 대장으로` : `${ym} 대장에 없음`}
                onClick={() => places && onOpen(ym, places[0])}
              />
            );
          })}
          <span className="ins-muted">{strip.length ? `${ymShort(strip[0])} ~ ${ymShort(strip[strip.length - 1])}` : ''}</span>
        </div>

        {person.current.length > 0 && (
          <section>
            <h5>현재 계약 <span className="ins-muted">{person.last} 기준</span></h5>
            <div className="ins-terms">
              {person.current.map((t) => <span key={t.label}><em>{t.label}</em>{fmt(t.type, t.value)}</span>)}
            </div>
          </section>
        )}

        <section>
          <h5>계약 변경 이력 <span className="ins-muted">누르면 그 달 대장으로</span></h5>
          <ol className="ins-timeline">
            {[...person.contracts].reverse().map((c) => (
              <li key={`${c.ym}-${c.kind}`} className={c.kind}>
                <button type="button" className="ins-ym" onClick={() => onOpen(c.ym, { sheet: c.sheet, row_id: c.row_id })}>{c.ym}</button>
                {c.kind === 'start' ? (
                  <div><b>처음 등장</b> <span className="ins-muted">{SHEET_LABEL[c.sheet]} · 계약 항목 {c.terms.length}개</span></div>
                ) : (
                  <ul>
                    {c.changes.map((x) => <li key={x.label}><em>{x.label}</em> <s>{fmt(x.type, x.before)}</s> → <b>{fmt(x.type, x.after)}</b></li>)}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        </section>

        {person.history.length > 0 && (
          <section>
            <h5>성명·주민번호 이력</h5>
            <ul className="ins-list">
              {[...person.history].reverse().map((h, i) => (
                <li key={`${h.at}-${i}`}>
                  <span className="ins-muted">{String(h.at || '').slice(0, 10)}</span> <b>{OP_LABEL[h.op] || h.op}</b>{' '}
                  {h.before != null && <><s>{Array.isArray(h.before) ? h.before.join(', ') : String(h.before)}</s> → </>}
                  {h.after != null && <b>{String(h.after)}</b>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {person.leaves.length > 0 && (
          <section>
            <h5>휴직</h5>
            <ul className="ins-list">{person.leaves.map((x) => <li key={x.id}><b>{x.reason || '휴직'}</b> {x.start} ~ {x.end || '(종료일 없음)'}</li>)}</ul>
          </section>
        )}

        {person.memo && (
          <section>
            <h5>메모</h5>
            <p className="ins-memo">{person.memo.text}</p>
          </section>
        )}

        {others.length > 0 && (
          <section className="ins-suspect">
            <h5>같은 이름의 다른 직원 {others.length}명</h5>
            {group.overlap && <p className="ins-warn">같은 달 같은 시트에 함께 있은 적이 있어 다른 사람일 가능성이 높습니다.</p>}
            {others.map((o) => (
              <div key={o.employee_id} className="ins-suspect-row">
                <span>{o.name} · {o.id_number || '주민번호 없음'} · {o.first} ~ {o.last}</span>
                {arm === o.employee_id ? (
                  <>
                    <button type="button" className="ins-btn danger" disabled={busy} onClick={() => merge(o)}>병합 확정</button>
                    <button type="button" className="ins-btn" disabled={busy} onClick={() => setArm(null)}>아니오</button>
                  </>
                ) : (
                  <button type="button" className="ins-btn" onClick={() => setArm(o.employee_id)} title="그 직원의 모든 달 줄과 메모를 이 직원으로 옮깁니다">같은 사람 — 병합</button>
                )}
              </div>
            ))}
            {error && <p className="ins-warn">{error}</p>}
          </section>
        )}
      </aside>
    </>
  );
}

// ---------------------------------------------------------------- 직원으로 묶이지 않은 줄
function Unlinked({ items, people, companyId, onOpen, onLinked }) {
  const [choice, setChoice] = useState({});
  const [error, setError] = useState('');
  const link = async (item, employeeId) => {
    setError('');
    try {
      await apiPost(`${API}/companies/${companyId}/employees/link`, { row_id: item.row_id, employee_id: employeeId || null });
      onLinked();
    } catch (e) {
      setError(e.message);
    }
  };
  const sorted = [...people.values()].sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  return (
    <div className="ins-unlinked">
      {error && <p className="ins-warn">{error}</p>}
      {items.map((item) => {
        const same = sorted.filter((p) => p.name === item.name);
        const picked = choice[item.row_id] !== undefined ? choice[item.row_id] : (same[0] ? same[0].employee_id : '');
        return (
          <div key={item.row_id} className="ins-unlinked-row">
            <button type="button" className="ins-ym" onClick={() => onOpen(item.ym, item)}>{item.ym}</button>
            <span><b>{item.name}</b> <span className="ins-muted">{SHEET_LABEL[item.sheet]} · {item.reason}</span></span>
            <select value={picked} onChange={(e) => setChoice({ ...choice, [item.row_id]: e.target.value })}>
              <option value="">연결할 직원…</option>
              {sorted.map((p) => <option key={p.employee_id} value={p.employee_id}>{p.name} · {p.first}~{p.last}</option>)}
            </select>
            <button type="button" className="ins-btn" disabled={!picked} onClick={() => link(item, picked)}>연결</button>
            <button type="button" className="ins-btn" onClick={() => link(item, null)}>새 직원</button>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- 화면
function InsightsView({ companyId, onOpenRow }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [showUnlinked, setShowUnlinked] = useState(false);
  // 화면 상태는 주소(URL)에 둔다: 주소를 그대로 보내면 같은 화면이 열린다.
  // person = 연 직원, status = 직원 목록 필터(기본 재직), q = 찾는 말
  const [params, setParams] = useSearchParams();
  const selected = params.get('person');
  const filter = params.get('status') || '재직';
  const query = params.get('q') || '';
  const setParam = useCallback((key, value, replace = false) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value == null || value === '') next.delete(key); else next.set(key, value);
      return next;
    }, { replace });
  }, [setParams]);
  const setSelected = useCallback((id) => setParam('person', id), [setParam]);
  const setFilter = (value) => setParam('status', value === '재직' ? null : value, true);
  const setQuery = (value) => setParam('q', value, true);

  const load = useCallback(async () => {
    try {
      setData(await apiGet(`${API}/companies/${companyId}/employees`));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [companyId]);
  useEffect(() => { setData(null); load(); }, [load]);

  const people = useMemo(() => new Map((data ? data.employees : []).map((p) => [p.employee_id, p])), [data]);

  // 인사이트: 그래프용 달별 값, 핵심 숫자, 챙겨야 할 것, 최근 변화
  const insight = useMemo(() => {
    if (!data || !data.months.length) return null;
    const today = toDate(data.today);
    const latest = data.months[data.months.length - 1];
    const byYm = new Map(data.monthly.map((m) => [m.ym, m]));
    const window = data.months.slice(-24);
    const sheets = ['fulltime', 'daily', 'business'].filter((s) => data.monthly.some((m) => m[s] > 0));

    const hiresIn = (ym) => data.employees.filter((p) => String(p.hire_date || '').slice(0, 7) === ym).length;
    const exitsIn = (ym) => data.employees.filter((p) => String(p.leave_date || '').slice(0, 7) === ym).length;
    const flow = window.map((ym) => ({ ym: ymShort(ym), 입사: hiresIn(ym), 퇴사: -exitsIn(ym) }));
    const heads = window.map((ym) => ({ ym: ymShort(ym), ...Object.fromEntries(sheets.map((s) => [s, (byYm.get(ym) || {})[s] || 0])) }));
    const pays = window.map((ym) => ({
      ym: ymShort(ym),
      인건비: Math.round((byYm.get(ym) || {}).gross || 0),
      퇴직금: Math.round((byYm.get(ym) || {}).severance || 0),
    }));
    // 최근 12개월 합: 인건비 + 퇴직금 적립(추정)
    const last12 = data.months.slice(-12).map((ym) => byYm.get(ym) || {});
    const year12 = { pay: last12.reduce((a, m) => a + (m.gross || 0), 0), severance: last12.reduce((a, m) => a + (m.severance || 0), 0), months: last12.length };

    const now = byYm.get(latest);
    const yearAgo = byYm.get(shiftYm(latest, -12));
    const year = latest.slice(0, 4);
    const active = data.employees.filter((p) => p.status === '재직');
    const tenures = active.map((p) => tenureMonths(p.hire_date)).filter((m) => m != null);
    const avgTenure = tenures.length ? tenures.reduce((a, b) => a + b, 0) / tenures.length : null;

    const watch = [];
    const within = (d, days) => d && d >= today && d - today <= days * DAY;
    active.forEach((p) => {
      const hire = toDate(p.hire_date);
      const anniversary = hire && addYears(hire, 1);
      if (within(anniversary, 90)) watch.push({ person: p, kind: 'year', at: anniversary, text: '근속 1년 도래 — 이날부터 퇴직금이 생깁니다' });
      const end = p.contract_end && toDate(p.contract_end);
      if (end && end - today >= -30 * DAY && end - today <= 60 * DAY) watch.push({ person: p, kind: 'contract', at: end, text: '계약 종료일 — 갱신·종료를 확인하세요' });
      const intern = p.intern_end && toDate(p.intern_end);
      if (within(intern, 45)) watch.push({ person: p, kind: 'intern', at: intern, text: '인턴(수습) 종료' });
      const birth = p.birth && toDate(p.birth);
      const sixty = birth && addYears(birth, 60);
      if (within(sixty, 180)) watch.push({ person: p, kind: 'sixty', at: sixty, text: '만 60세 도래 — 국민연금 의무가입이 끝납니다' });
      const leaving = p.leave_date && toDate(p.leave_date);
      if (leaving && leaving >= today) watch.push({ person: p, kind: 'leaving', at: leaving, text: '퇴사 예정(마지막 근무일)' });
      p.leaves.forEach((x) => {
        const s = toDate(x.start);
        const e = x.end ? toDate(x.end) : null;
        if (s && s <= today && (!e || e >= today)) watch.push({ person: p, kind: 'leave', at: e || s, text: `${x.reason || '휴직'} 중${e ? ` · ${x.end}까지` : ''}` });
      });
    });
    watch.sort((a, b) => a.at - b.at);

    const since = shiftYm(latest, -5);
    const feed = [];
    data.employees.forEach((p) => {
      if (p.hire_date && String(p.hire_date).slice(0, 7) >= since) feed.push({ person: p, at: String(p.hire_date).slice(0, 10), kind: 'hire', text: `입사 · ${SHEET_LABEL[p.last_sheet]}` });
      if (p.leave_date && String(p.leave_date).slice(0, 7) >= since) feed.push({ person: p, at: String(p.leave_date).slice(0, 10), kind: 'exit', text: '퇴사' });
      p.contracts.filter((c) => c.kind === 'change' && c.ym >= since).forEach((c) => feed.push({
        person: p, at: `${c.ym}-01`, ym: c.ym, kind: 'change',
        text: c.changes.slice(0, 2).map((x) => `${x.label} ${fmt(x.type, x.before)} → ${fmt(x.type, x.after)}`).join(' · ') + (c.changes.length > 2 ? ` 외 ${c.changes.length - 2}건` : ''),
      }));
      p.history.filter((h) => String(h.at || '').slice(0, 7) >= since).forEach((h) => feed.push({
        person: p, at: String(h.at).slice(0, 10), kind: 'identity', text: `${OP_LABEL[h.op] || h.op}${h.after ? ` → ${h.after}` : ''}`,
      }));
    });
    feed.sort((a, b) => (a.at < b.at ? 1 : -1));

    return {
      latest, sheets, flow, heads, pays, watch, feed,
      headcount: now ? now.headcount : 0,
      headDelta: now && yearAgo ? now.headcount - yearAgo.headcount : null,
      hiresYear: data.employees.filter((p) => String(p.hire_date || '').startsWith(year)).length,
      exitsYear: data.employees.filter((p) => String(p.leave_date || '').startsWith(year)).length,
      year,
      avgTenure,
      pay: now ? now.gross : 0,
      severance: now ? now.severance : 0,
      year12,
      payDelta: now && yearAgo && yearAgo.gross ? (now.gross - yearAgo.gross) / yearAgo.gross : null,
    };
  }, [data]);

  const counts = useMemo(() => {
    const c = { 재직: 0, '명단에 없음': 0, 퇴사: 0, 전체: 0 };
    (data ? data.employees : []).forEach((p) => { c.전체 += 1; c[p.status] += 1; });
    return c;
  }, [data]);
  const shown = useMemo(() => {
    if (!data) return [];
    const q = query.trim();
    return data.employees.filter((p) => (filter === '전체' || p.status === filter) && (!q || p.name.includes(q) || String(p.id_number || '').includes(q)));
  }, [data, filter, query]);

  if (error) return <div className="ins"><p className="ins-warn">{error}</p></div>;
  if (!data) return <div className="ins"><p className="ins-muted">직원을 모으는 중…</p></div>;
  if (!insight) return <div className="ins"><p className="ins-muted">이 기업에는 아직 임금대장이 없습니다.</p></div>;

  const open = (ym, place) => { const { year, month } = parseYm(ym); onOpenRow({ year, month, sheet: place.sheet, row_id: place.row_id }); };
  const checks = data.unlinked.length + data.suspects.filter((s) => !s.overlap).length;
  const person = selected ? people.get(selected) : null;

  return (
    <div className="ins">
      <div className="ins-stats">
        <Stat label={`재직 인원 · ${insight.latest}`} value={`${insight.headcount}명`}
          sub={insight.headDelta == null ? '작년 같은 달 대장 없음' : `작년 같은 달보다 ${insight.headDelta > 0 ? '+' : ''}${insight.headDelta}명`} />
        <Stat label={`${insight.year}년 입사 · 퇴사`} value={`${insight.hiresYear} · ${insight.exitsYear}명`} sub="입사일·퇴사일 기준" />
        <Stat label="재직자 평균 근속" value={insight.avgTenure == null ? '–' : tenureText(Math.round(insight.avgTenure))} sub={`재직 ${counts.재직}명 기준`} />
        <Stat label={`인건비 · ${insight.latest}`} value={manwon(insight.pay) + '원'}
          sub={`${insight.payDelta == null ? '작년 같은 달 대장 없음' : `작년 같은 달보다 ${insight.payDelta > 0 ? '+' : ''}${(insight.payDelta * 100).toFixed(1)}%`} · 퇴직금 적립 ${manwon(insight.severance)}원(추정)`}
          tone={insight.payDelta == null ? '' : insight.payDelta > 0 ? 'up' : 'down'} />
        <Stat label={`최근 ${insight.year12.months}개월 인건비`} value={manwon(insight.year12.pay + insight.year12.severance) + '원'}
          sub={`지급 ${manwon(insight.year12.pay)} + 퇴직금 적립 ${manwon(insight.year12.severance)}(추정)`} />
      </div>

      <div className="ins-charts">
        <ChartCard title="인원 추이" note="달마다 대장에 있는 인원">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={insight.heads} margin={{ top: 6, right: 8, bottom: 0, left: -18 }}>
              <CartesianGrid stroke="#eef0f2" vertical={false} />
              <XAxis dataKey="ym" tick={axis} tickLine={false} axisLine={{ stroke: '#dee2e6' }} interval="preserveStartEnd" minTickGap={18} />
              <YAxis tick={axis} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip content={<ChartTip />} />
              {insight.sheets.length > 1 && <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />}
              {insight.sheets.map((s) => (
                <Area key={s} type="monotone" dataKey={s} name={SHEET_LABEL[s]} stackId="1" stroke={SERIES[s]} strokeWidth={2} fill={SERIES[s]} fillOpacity={0.18} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="입·퇴사 흐름" note="위 입사 · 아래 퇴사">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={insight.flow} margin={{ top: 6, right: 8, bottom: 0, left: -18 }} stackOffset="sign" barCategoryGap="28%">
              <CartesianGrid stroke="#eef0f2" vertical={false} />
              <XAxis dataKey="ym" tick={axis} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={18} />
              <YAxis tick={axis} tickLine={false} axisLine={false} allowDecimals={false} tickFormatter={(v) => Math.abs(v)} />
              <ReferenceLine y={0} stroke="#adb5bd" />
              <Tooltip content={<ChartTip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="입사" name="입사" fill={HIRE} stackId="f" radius={[4, 4, 0, 0]} />
              <Bar dataKey="퇴사" name="퇴사" fill={EXIT} stackId="f" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="월 인건비 · 퇴직금 적립" note="지급(세전·수수료) + 퇴직금 적립 추정(상용직 임금총액 ÷ 12)">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={insight.pays} margin={{ top: 6, right: 8, bottom: 0, left: 4 }} barCategoryGap="24%">
              <CartesianGrid stroke="#eef0f2" vertical={false} />
              <XAxis dataKey="ym" tick={axis} tickLine={false} axisLine={{ stroke: '#dee2e6' }} interval="preserveStartEnd" minTickGap={18} />
              <YAxis tick={axis} tickLine={false} axisLine={false} tickFormatter={manwon} width={48} />
              <Tooltip content={<ChartTip money />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="인건비" name="인건비(지급)" stackId="c" fill={SERIES.fulltime} />
              <Bar dataKey="퇴직금" name="퇴직금 적립(추정)" stackId="c" fill={SERIES.daily} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="ins-lower">
        <section className="ins-card ins-watch">
          <header><h4>챙겨야 할 것</h4><span className="ins-muted">오늘 {data.today} 기준</span></header>
          <div className="ins-scroll">
            {insight.watch.length === 0 && checks === 0 && <p className="ins-empty">지금 챙길 일이 없습니다.</p>}
            {insight.watch.map((w, i) => (
              <button type="button" key={`${w.person.employee_id}-${w.kind}-${i}`} className={`ins-watch-item kind-${w.kind}`} onClick={() => setSelected(w.person.employee_id)}>
                <span className="ins-watch-date">{iso(w.at).slice(5)}<em>{dday(w.at, toDate(data.today))}</em></span>
                <span><b>{w.person.name}</b> {w.text}</span>
              </button>
            ))}
            {checks > 0 && (
              <div className="ins-watch-checks">
                <button type="button" className="ins-watch-item kind-check" onClick={() => setShowUnlinked(!showUnlinked)}>
                  <span className="ins-watch-date">확인<em>필요</em></span>
                  <span>
                    {data.unlinked.length > 0 && <>직원으로 묶이지 않은 줄 <b>{data.unlinked.length}개</b> </>}
                    {data.suspects.filter((s) => !s.overlap).length > 0 && <>· 같은 사람일 수 있는 직원 <b>{data.suspects.filter((s) => !s.overlap).length}쌍</b></>}
                  </span>
                </button>
                {showUnlinked && (
                  <>
                    {data.unlinked.length > 0 && <Unlinked items={data.unlinked} people={people} companyId={companyId} onOpen={open} onLinked={load} />}
                    {data.suspects.filter((s) => !s.overlap).map((s) => (
                      <button type="button" key={s.name} className="ins-btn" onClick={() => setSelected(s.employee_ids[0])}>{s.name} {s.employee_ids.length}명 비교</button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        <section className="ins-card ins-feed">
          <header><h4>최근 변화</h4><span className="ins-muted">최근 6개월 · 입사·퇴사·계약 변경·신원</span></header>
          <ol className="ins-scroll">
            {insight.feed.length === 0 && <p className="ins-empty">최근 6개월 동안 변화가 없습니다.</p>}
            {insight.feed.map((f, i) => (
              <li key={`${f.person.employee_id}-${f.kind}-${f.at}-${i}`} className={`kind-${f.kind}`}>
                <button type="button" onClick={() => setSelected(f.person.employee_id)}>
                  <span className="ins-feed-date">{f.kind === 'change' ? f.ym : f.at}</span>
                  <b>{f.person.name}</b> <span className="ins-feed-text">{f.text}</span>
                </button>
              </li>
            ))}
          </ol>
        </section>

        <section className="ins-card ins-people">
          <header>
            <h4>직원</h4>
            <div className="ins-seg">
              {['재직', '명단에 없음', '퇴사', '전체'].map((f) => (
                <button type="button" key={f} className={filter === f ? 'on' : ''} onClick={() => setFilter(f)}>{f} <b>{counts[f]}</b></button>
              ))}
            </div>
            <input className="ins-search" value={query} placeholder="이름·주민번호" onChange={(e) => setQuery(e.target.value)} />
          </header>
          <div className="ins-scroll ins-cards">
            {shown.length === 0 && <p className="ins-empty">해당하는 직원이 없습니다.</p>}
            {shown.map((p) => (
              <button type="button" key={p.employee_id} className={`ins-person ${selected === p.employee_id ? 'on' : ''}`} onClick={() => setSelected(p.employee_id)}>
                <span className="ins-person-top">
                  <b>{p.name}</b>
                  <span className={`ins-pill ${STATUS_TONE[p.status]}`}>{p.status}</span>
                  {p.memo && <span className="ins-flag" title={p.memo.text}>메모</span>}
                </span>
                <span className="ins-person-sub">
                  <i className={`dot sheet-${p.last_sheet}`} />{SHEET_LABEL[p.last_sheet]} · 근속 {tenureText(tenureMonths(p.hire_date, p.leave_date))}
                </span>
                <span className="ins-person-sub">
                  {p.last_pay != null ? `최근 ${manwon(p.last_pay)}원` : ''}
                  {p.contracts.filter((c) => c.kind === 'change').length > 0 && ` · 계약 변경 ${p.contracts.filter((c) => c.kind === 'change').length}회`}
                </span>
              </button>
            ))}
          </div>
        </section>
      </div>

      <Detail
        person={person}
        suspects={data.suspects}
        people={people}
        companyId={companyId}
        months={data.months}
        onOpen={open}
        onMerged={load}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

export default InsightsView;
